import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { branchesUrl, branchByIdUrl, branchStatusUrl, usersUrl } from '../../BaseURL';
import Modal from '../../components/Modal';
import { fmtDate } from '../../utils/format';
import { toastSuccess, swalDangerConfirm, swalSuccess, showAxiosError } from '../../utils/swal';

const GH_REGIONS = ['Greater Accra','Ashanti','Western','Western North','Central','Eastern','Volta','Oti','Northern','Savannah','North East','Upper East','Upper West','Bono','Bono East','Ahafo'];

const emptyBranch = { branch_code: '', name: '', region: 'Greater Accra', address: '', phone: '', email: '', default_markup_percent: '' };
const emptyManager = { full_name: '', username: '', email: '', phone: '', temp_password: '' };

const generatePassword = () => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$&*';
  let out = '';
  for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

const initials = (name) => String(name || '?')
  .split(/\s+/).filter(Boolean).slice(0, 2)
  .map((p) => p[0].toUpperCase()).join('') || '?';

const Branches = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('cards'); // 'cards' | 'table'
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null); // null = create, branch id = edit
  const [form, setForm] = useState(emptyBranch);
  const [managerMode, setManagerMode] = useState('new');
  const [managerForm, setManagerForm] = useState(emptyManager);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [b, u] = await Promise.all([axios.get(branchesUrl), axios.get(usersUrl)]);
      if (b.data?.success) setRows(b.data.results || []);
      if (u.data?.success) setUsers(u.data.results || []);
    } catch (_) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const usersById = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  const decorated = useMemo(() => rows.map((b) => {
    const manager = b.manager_user_id ? usersById.get(b.manager_user_id) : null;
    const staffCount = users.filter((u) => u.branch_id === b.id && u.is_active).length;
    return { ...b, manager, staffCount };
  }), [rows, usersById, users]);

  const filtered = useMemo(() => {
    if (!search.trim()) return decorated;
    const q = search.trim().toLowerCase();
    return decorated.filter((b) =>
      [b.name, b.branch_code, b.region, b.address, b.manager?.full_name].some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [decorated, search]);

  const stats = useMemo(() => ({
    total: decorated.length,
    active: decorated.filter((b) => b.is_active).length,
    inactive: decorated.filter((b) => !b.is_active).length,
    head_office: decorated.filter((b) => b.is_head_office).length,
    unassigned: decorated.filter((b) => !b.manager_user_id).length,
  }), [decorated]);

  const eligibleUsers = useMemo(
    () => users.filter((u) => u.is_active === 1 && u.role !== 'super_admin'),
    [users]
  );

  const openCreate = () => {
    setEditId(null);
    setForm(emptyBranch);
    setManagerForm({ ...emptyManager, temp_password: generatePassword() });
    setSelectedManagerId('');
    setManagerMode('new');
    setError(null);
    setOpen(true);
  };

  const openEdit = (b) => {
    setEditId(b.id);
    setForm({
      branch_code: b.branch_code || '',
      name: b.name || '',
      region: b.region || 'Greater Accra',
      address: b.address || '',
      phone: b.phone || '',
      email: b.email || '',
      default_markup_percent: b.default_markup_percent != null ? String(b.default_markup_percent) : '',
    });
    setError(null);
    setOpen(true);
  };

  const submit = async () => {
    setError(null);
    if (!form.name) { setError('Branch name is required'); return; }

    // Edit path — only updates branch details. Manager and code are managed elsewhere.
    if (editId) {
      setSaving(true);
      try {
        const res = await axios.patch(branchByIdUrl(editId), {
          name: form.name,
          region: form.region,
          address: form.address,
          phone: form.phone,
          email: form.email,
          default_markup_percent: form.default_markup_percent === '' ? null : Number(form.default_markup_percent),
        });
        if (res.data?.success) {
          setOpen(false);
          await load();
          toastSuccess('Branch updated');
        } else {
          setError(res.data?.message || 'Failed');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed');
      } finally { setSaving(false); }
      return;
    }

    // Create path — requires manager.
    if (!form.branch_code) { setError('Branch code is required'); return; }
    let payload = {
      ...form,
      default_markup_percent: form.default_markup_percent === '' ? null : Number(form.default_markup_percent),
    };
    if (managerMode === 'existing') {
      if (!selectedManagerId) { setError('Pick a user to assign as manager'); return; }
      payload.manager_user_id = Number(selectedManagerId);
    } else {
      const m = managerForm;
      if (!m.full_name || !m.username || !m.email || !m.temp_password) {
        setError('Fill in all manager fields'); return;
      }
      if (m.temp_password.length < 8) {
        setError('Manager temporary password must be at least 8 characters'); return;
      }
      payload.new_manager = m;
    }
    setSaving(true);
    try {
      const res = await axios.post(branchesUrl, payload);
      if (res.data?.success) {
        setOpen(false);
        await load();
        if (managerMode === 'new') {
          swalSuccess(
            'Branch created',
            `Share the manager's temporary password — they must change it on first sign-in.\n\nPassword: ${managerForm.temp_password}`
          );
        } else {
          toastSuccess(res.data.message || 'Branch created');
        }
      } else {
        setError(res.data?.message || 'Failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (b) => {
    const action = b.is_active ? 'suspend' : 'activate';
    const ok = await swalDangerConfirm(
      `${action === 'suspend' ? 'Suspend' : 'Activate'} this branch?`,
      action === 'suspend'
        ? `"${b.name}" will be hidden from cashiers and stop accepting sales until you reactivate it.`
        : `"${b.name}" will start accepting sales again.`,
      { confirmButtonText: action === 'suspend' ? 'Yes, suspend' : 'Yes, activate' }
    );
    if (!ok) return;
    try {
      await axios.patch(branchStatusUrl(b.id), { is_active: !b.is_active });
      toastSuccess(`Branch ${b.is_active ? 'suspended' : 'activated'}`);
      load();
    } catch (err) { showAxiosError(err, 'Could not update branch'); }
  };

  return (
    <>
      {/* STATS STRIP */}
      <div className="br-stats">
        <div className="br-stat tone-brand">
          <div className="lbl">Total branches</div>
          <div className="val">{stats.total}</div>
          <div className="hint">{stats.head_office} head office</div>
        </div>
        <div className="br-stat tone-success">
          <div className="lbl">Active</div>
          <div className="val">{stats.active}</div>
          <div className="hint">{stats.inactive} suspended</div>
        </div>
        <div className={`br-stat ${stats.unassigned > 0 ? 'tone-warn' : 'tone-mute'}`}>
          <div className="lbl">Without manager</div>
          <div className="val">{stats.unassigned}</div>
          <div className="hint">{stats.unassigned === 0 ? 'All covered' : 'needs attention'}</div>
        </div>
        <div className="br-stat tone-accent">
          <div className="lbl">Staff across branches</div>
          <div className="val">{users.filter((u) => u.is_active).length}</div>
          <div className="hint">active accounts</div>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="panel">
        <div className="panel-head">
          <div>
            <h2>Branches</h2>
            <p className="muted-sm" style={{ margin: '2px 0 0' }}>Manage your shops across the country. Every branch has its own manager and staff.</p>
          </div>
          <div className="panel-toolbar">
            <div className="br-search">
              <span className="ic">🔍</span>
              <input
                className="f-input"
                placeholder="Search by name, code, manager, region…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="br-view-toggle" role="tablist">
              <button
                type="button"
                className={`view-btn ${view === 'cards' ? 'active' : ''}`}
                onClick={() => setView('cards')}
                title="Card view"
              >▦</button>
              <button
                type="button"
                className={`view-btn ${view === 'table' ? 'active' : ''}`}
                onClick={() => setView('table')}
                title="Table view"
              >☰</button>
            </div>
            <button className="btn-primary" onClick={openCreate}>+ New branch</button>
          </div>
        </div>

        {loading ? (
          <p style={{ color: 'var(--mute)' }}>Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="br-empty">
            <div className="ic">🏬</div>
            <h3>{search ? 'No branches match that search' : 'No branches yet'}</h3>
            <p>{search ? 'Try a different keyword.' : 'Add your first branch to start running multi-location operations.'}</p>
            {!search && <button className="btn-primary" onClick={openCreate}>+ Create your first branch</button>}
          </div>
        ) : view === 'cards' ? (
          <div className="br-grid">
            {filtered.map((b, i) => (
              <motion.div
                key={b.id}
                className={`br-card ${!b.is_active ? 'suspended' : ''}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.03 }}
              >
                <div className="br-card-top">
                  <div className="br-code">
                    {b.is_head_office ? <span className="hq-pill">HQ</span> : null}
                    <span className="code">{b.branch_code}</span>
                  </div>
                  <div className={`br-status ${b.is_active ? 'on' : 'off'}`}>
                    <span className="dot" />
                    {b.is_active ? 'Active' : 'Suspended'}
                  </div>
                </div>

                <h3 className="br-name">{b.name}</h3>
                <div className="br-region">📍 {b.region || 'No region set'}</div>
                {b.address && <div className="br-meta-row">{b.address}</div>}

                <div className="br-mgr">
                  {b.manager ? (
                    <>
                      <div className="avatar">{initials(b.manager.full_name)}</div>
                      <div className="mgr-info">
                        <div className="role-tag">Branch manager</div>
                        <div className="name">{b.manager.full_name}</div>
                        {b.manager.email && <div className="contact">{b.manager.email}</div>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="avatar danger">!</div>
                      <div className="mgr-info">
                        <div className="role-tag danger">Manager required</div>
                        <div className="name danger">Unassigned</div>
                        <div className="contact">Add a manager to operate this branch</div>
                      </div>
                    </>
                  )}
                </div>

                <div className="br-foot">
                  <div className="br-foot-chips">
                    {b.phone && <span className="chip">📞 {b.phone}</span>}
                    <span className="chip">👥 {b.staffCount} staff</span>
                    {b.default_markup_percent != null && (
                      <span className="chip brand">📈 {Number(b.default_markup_percent).toFixed(0)}% markup</span>
                    )}
                    <span className="chip muted">Since {fmtDate(b.created_at)?.split(',')[0] || '—'}</span>
                  </div>
                  <div className="br-actions">
                    <button className="br-action edit" onClick={() => openEdit(b)} title="Edit branch details">
                      ✎ Edit
                    </button>
                    <button
                      className="br-action"
                      onClick={() => toggleStatus(b)}
                      disabled={b.is_head_office === 1}
                      title={b.is_head_office ? 'Head office cannot be suspended' : ''}
                    >
                      {b.is_active ? 'Suspend' : 'Activate'}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Code</th><th>Name</th><th>Manager</th><th>Region</th>
                  <th>Phone</th><th>Status</th><th>Created</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <strong>{b.branch_code}</strong>
                      {b.is_head_office ? <span style={{ marginLeft: 8 }} className="badge badge-gold">HQ</span> : null}
                    </td>
                    <td><strong>{b.name}</strong></td>
                    <td>
                      {b.manager ? b.manager.full_name : <span style={{ color: 'var(--danger)' }}>Unassigned</span>}
                    </td>
                    <td>{b.region || '—'}</td>
                    <td>{b.phone || '—'}</td>
                    <td>{b.is_active ? <span className="badge badge-green">Active</span> : <span className="badge badge-red">Suspended</span>}</td>
                    <td>{fmtDate(b.created_at)}</td>
                    <td className="right" style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-link" onClick={() => openEdit(b)}>Edit</button>
                      <button className="btn-link" onClick={() => toggleStatus(b)} disabled={b.is_head_office === 1}>
                        {b.is_active ? 'Suspend' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        title={editId ? `Edit branch — ${form.name || ''}` : 'Create a new branch'}
        open={open} onClose={() => setOpen(false)}
        width={editId ? 640 : 1040}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : (editId ? 'Save changes' : 'Create branch')}
            </button>
          </>
        }
      >
        {error && <div className="notice">{error}</div>}

        <div className={`branch-modal ${editId ? 'edit-mode' : ''}`}>
          <section className="branch-modal-col">
            <header className="branch-modal-section-head">
              {!editId && <div className="num">1</div>}
              <div>
                <h4>Branch details</h4>
                <p>{editId
                  ? 'Branch code and manager are managed elsewhere — update contact and location here.'
                  : 'Where the shop is, how to reach it.'}</p>
              </div>
            </header>

            <div className="f-grid f-grid-2">
              <div className="f-row">
                <label className="f-label">Branch code {!editId && '*'}</label>
                <input
                  className="f-input"
                  value={form.branch_code}
                  onChange={(e) => setForm({ ...form, branch_code: e.target.value.toUpperCase() })}
                  placeholder="ACC, KMA, TKR"
                  disabled={!!editId}
                  title={editId ? 'Branch code cannot be changed once created' : ''}
                />
              </div>
              <div className="f-row">
                <label className="f-label">Branch name *</label>
                <input className="f-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="East Legon Branch" />
              </div>
              <div className="f-row">
                <label className="f-label">Region</label>
                <select className="f-select" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
                  {GH_REGIONS.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div className="f-row">
                <label className="f-label">Phone</label>
                <input className="f-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0244 000 000" />
              </div>
              <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                <label className="f-label">Address</label>
                <input className="f-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Lashibi, Accra" />
              </div>
              <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                <label className="f-label">Email</label>
                <input className="f-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="branch@yourshop.com" />
              </div>
              <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                <label className="f-label">
                  Default markup % <span style={{ color: 'var(--mute-2)', textTransform: 'none', fontWeight: 500 }}>(applies to every category)</span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    className="f-input"
                    type="number" min="0" step="0.01"
                    value={form.default_markup_percent}
                    onChange={(e) => setForm({ ...form, default_markup_percent: e.target.value })}
                    placeholder="e.g. 30"
                    style={{ maxWidth: 180 }}
                  />
                  <span style={{ color: 'var(--mute)', fontSize: 13.5 }}>
                    Cost × (1 + markup ÷ 100). Override per category later in <strong>Settings</strong>.
                  </span>
                </div>
              </div>
            </div>

            {editId && (
              <div className="edit-mode-hint">
                💡 To change the branch manager, open <strong>Users & Access</strong>. To suspend or activate, use the action on the branch card.
              </div>
            )}
          </section>

          {!editId && <section className="branch-modal-col branch-modal-manager">
            <header className="branch-modal-section-head">
              <div className="num">2</div>
              <div>
                <h4>Branch manager <span className="req">required</span></h4>
                <p>Every branch needs someone in charge.</p>
              </div>
            </header>

            <div className="branch-mgr-tabs" role="tablist">
              <button
                type="button" role="tab" aria-selected={managerMode === 'new'}
                className={`branch-mgr-tab ${managerMode === 'new' ? 'active' : ''}`}
                onClick={() => setManagerMode('new')}
              >
                <span className="ic">➕</span><span>Create new</span>
              </button>
              <button
                type="button" role="tab" aria-selected={managerMode === 'existing'}
                className={`branch-mgr-tab ${managerMode === 'existing' ? 'active' : ''}`}
                onClick={() => setManagerMode('existing')}
              >
                <span className="ic">👤</span><span>Pick existing</span>
              </button>
            </div>

            {managerMode === 'new' ? (
              <div className="f-grid f-grid-2">
                <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                  <label className="f-label">Full name *</label>
                  <input className="f-input" value={managerForm.full_name} onChange={(e) => setManagerForm({ ...managerForm, full_name: e.target.value })} placeholder="Ama Owusu" />
                </div>
                <div className="f-row">
                  <label className="f-label">Username *</label>
                  <input className="f-input" value={managerForm.username} onChange={(e) => setManagerForm({ ...managerForm, username: e.target.value })} placeholder="ama.owusu" />
                </div>
                <div className="f-row">
                  <label className="f-label">Phone</label>
                  <input className="f-input" value={managerForm.phone} onChange={(e) => setManagerForm({ ...managerForm, phone: e.target.value })} />
                </div>
                <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                  <label className="f-label">Email *</label>
                  <input className="f-input" type="email" value={managerForm.email} onChange={(e) => setManagerForm({ ...managerForm, email: e.target.value })} placeholder="ama@yourshop.com" />
                </div>
                <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                  <label className="f-label">Temporary password *</label>
                  <div className="branch-pw-row">
                    <input className="f-input" value={managerForm.temp_password} onChange={(e) => setManagerForm({ ...managerForm, temp_password: e.target.value })} />
                    <button type="button" className="btn-ghost" onClick={() => setManagerForm({ ...managerForm, temp_password: generatePassword() })}>Generate</button>
                  </div>
                  <p className="branch-pw-hint">Manager must change on first sign-in.</p>
                </div>
              </div>
            ) : (
              <div className="f-row" style={{ marginBottom: 0 }}>
                <label className="f-label">Choose an active user *</label>
                <select className="f-select" value={selectedManagerId} onChange={(e) => setSelectedManagerId(e.target.value)}>
                  <option value="">— Select user —</option>
                  {eligibleUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name} · {u.role}{u.branch_name ? ` · ${u.branch_name}` : ''}
                    </option>
                  ))}
                </select>
                <p className="branch-pw-hint">
                  They'll be moved to this branch and promoted to <strong>Branch Manager</strong>.
                </p>
              </div>
            )}
          </section>}
        </div>
      </Modal>
    </>
  );
};

export default Branches;
